import { useContext } from "react";
import type { Language } from "../api/Lang";
import { langContext, localeFromLang } from "../api/Lang";
import type { TopupDiscountConfig } from "../../functions/src/types";

const el = {
    otherAmountPlaceholder: "Άλλο ποσό",
    paymentErrorTitle: "Σφάλμα Πληρωμής",
    redirectToPaymentFailed:
        "Αποτυχία μετάβασης στη σελίδα πληρωμής. Παρακαλώ δοκιμάστε ξανά.",
    /** When Firebase Functions rejects top-up in demo (failed-precondition). */
    topUpNotAvailableInDemo: "Η ανανέωση δεν είναι διαθέσιμη στο περιβάλλον demo.",
    /** Fallback when `placeOrderPackage` fails unexpectedly (catch path). */
    packageOrderCompletionFailed: "Παρουσιάστηκε σφάλμα κατά την ολοκλήρωση της παραγγελίας.",
    specialOfferStudentHeading: "🎉 Ειδική Προσφορά για μαθητές!",
    creditsNonRefundableDisclaimer:
        "Τα Lunchbox credits θα πρέπει να καταναλωθούν μέχρι το τέλος του έτους (31/12) μέσω της εφαρμογής και δεν ανταλλάσσονται με χρήματα.",
    creditsMoreInfoLink: "Περισσότερες πληροφορίες...",
    creditsMoreInfoDlgTitle: "Επικοινωνία",
    creditsMoreInfoDlgEmailPrefix: "Αν έχεις οποιαδήποτε απορία, στείλε μας email στο ",
    creditsMoreInfoDlgOrCall: " ή κάλεσέ μας στο ",
    topupConfirmationTitle: "Επιβεβαίωση Ανανέωσης",
    /** Full-screen spinner while `topUp` callable runs (matches client timeout budget). */
    topUpConnectingPayment: "Σύνδεση με το σύστημα πληρωμών για ασφαλή μετάβαση στην τράπεζα…",
    topUpWaitSecondsLeft: (seconds: number) =>
        seconds <= 0
            ? "Αν δεν ανοίξει σύντομα η σελίδα πληρωμής, επιστρέψτε και δοκιμάστε ξανά."
            : `Περίπου ${seconds} δευτ. πριν σταματήσει η αναμονή της εφαρμογής.`,
    /** Dialog titles without emoji (OkDlg). */
    paymentPausedDlgTitle: "Προσωρινή παύση πληρωμών",
    walletCreditDelayDlgTitle: "Καθυστέρηση ενημέρωσης υπολοίπου",
    /** Long Viva notices (inline + dialogs). */
    vivaDisabledLongBody:
        "Επιλύουμε προσωρινά ένα πρόβλημα με τη Viva. Οι ανανεώσεις θα ενεργοποιηθούν ξανά μόλις αποκατασταθεί.",
    vivaDelayedLongBody:
        "Λόγω προσωρινών προβλημάτων με τη Viva, η πίστωση στο Lunchbox wallet μπορεί να εμφανιστεί έως και σε 4 ώρες. Πριν ξαναπληρώσετε, ελέγξτε αν η κάρτα σας έχει ήδη χρεωθεί.",
    temporaryPaymentIssueHeading: "⚠️ Προσωρινό Πρόβλημα με την Πληρωμή",
    walletUpdateDelayHeading: "⚠️ Καθυστέρηση ενημέρωσης υπολοίπου",
    durationUntil: (d: string) => `μέχρι ${d}`,
    durationForDays: (n: number) => `για ${n} ημέρες`,
    tierLineWithMax: (min: number, max: number, pct: number, durationSuffix: string) =>
        `Με την ανανέωση ${min}€-${max}€, λαμβάνετε ${pct}% έκπτωση ${durationSuffix}!`,
    tierLineMinPlus: (min: number, pct: number, durationSuffix: string) =>
        `Με την ανανέωση ${min}€+, λαμβάνετε ${pct}% έκπτωση ${durationSuffix}!`,
};

const en: typeof el = {
    otherAmountPlaceholder: "Enter amount",
    paymentErrorTitle: "Payment Error",
    redirectToPaymentFailed: "Failed to redirect to payment page. Please try again.",
    topUpNotAvailableInDemo: "Top-up is not available in demo environment.",
    packageOrderCompletionFailed: "An error occurred while completing the order.",
    specialOfferStudentHeading: "🎉 Special Offer for students!",
    creditsNonRefundableDisclaimer:
        "Lunchbox credits must be used by the end of the year (31/12) through the application and cannot be exchanged for money.",
    creditsMoreInfoLink: "More Info...",
    creditsMoreInfoDlgTitle: "Contact",
    creditsMoreInfoDlgEmailPrefix: "If you have any inquiries, please email us at ",
    creditsMoreInfoDlgOrCall: " or call us at ",
    topupConfirmationTitle: "Top-up Confirmation",
    topUpConnectingPayment: "Connecting to the payment system to open the secure bank page…",
    topUpWaitSecondsLeft: (seconds: number) =>
        seconds <= 0
            ? "If the payment page does not open shortly, go back and try again."
            : `About ${seconds}s before the app stops waiting.`,
    paymentPausedDlgTitle: "Temporary payment pause",
    walletCreditDelayDlgTitle: "Wallet balance update delay",
    vivaDisabledLongBody:
        "We are temporarily resolving an issue with Viva. Top-ups will be re-enabled once this is fixed.",
    vivaDelayedLongBody:
        "Due to temporary issues with Viva, the amount may take up to 4 hours to appear in your Lunchbox wallet. Before making another payment, check whether your card has already been charged.",
    temporaryPaymentIssueHeading: "⚠️ Temporary Payment Issue",
    walletUpdateDelayHeading: "⚠️ Wallet update delay",
    durationUntil: (d: string) => `until ${d}`,
    durationForDays: (n: number) => `for ${n} days`,
    tierLineWithMax: (min: number, max: number, pct: number, durationSuffix: string) =>
        `With a ${min}€-${max}€ top-up, you get ${pct}% discount ${durationSuffix}!`,
    tierLineMinPlus: (min: number, pct: number, durationSuffix: string) =>
        `With a ${min}€+ top-up, you get ${pct}% discount ${durationSuffix}!`,
};

const topupLangs = { el, en };

export type TopupStrings = typeof el;

export function useTopupStrings(): TopupStrings {
    const [lang] = useContext(langContext);
    return topupLangs[lang];
}

/** One localized line for a discount tier row (legacy TopUp tier copy). */
export function formatTopupDiscountTierLine(
    lang: Language,
    tier: NonNullable<TopupDiscountConfig["tiers"]>[number],
): string {
    const s = topupLangs[lang];
    const durationSuffix =
        tier.endDate != null
            ? s.durationUntil(new Date(tier.endDate).toLocaleDateString(localeFromLang(lang, "date")))
            : tier.durationDays != null
              ? s.durationForDays(tier.durationDays)
              : "";

    if (tier.maxAmount != null) {
        return s.tierLineWithMax(tier.minAmount, tier.maxAmount, tier.discountPercentage, durationSuffix);
    }
    return s.tierLineMinPlus(tier.minAmount, tier.discountPercentage, durationSuffix);
}
